import React, { useRef, useState } from 'react';
import AvatarEditor from 'react-avatar-editor';
import Dropzone from 'react-dropzone';

import { getCurrentUser } from '~/api/currentUser';
import userProfileEmpty from '~/assets/images/user-profile-empty.svg';
import { Button } from '~/eds/src/components/Button/Button';
import { Grid } from '~/eds/src/components/Grid/Grid';
import { useDeleteModal } from '~/eds/src/components/hooks/useDeleteModal';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Image } from '~/eds/src/components/Image/Image';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { FormattedMessage, useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';

import { ERROR, SUCCESS } from '../../../../types/toast.types';
import { showToast } from '../../../Shared/EcToast/showToast';
import { removePic, updatePic } from './AvatarPanel.data';
import { avatarSize, border, layoutSize, styles } from './AvatarPanel.styles';

export const AvatarPanel = ({ user, setCurrentUser }) => {
  const [image, setImage] = useState('');
  const dropzoneRef = useRef(null);
  const editorRef = useRef(null);
  const [scale, setScale] = useState(1.2);
  const intl = useIntl();

  const handleDrop = (dropped) => {
    setImage(dropped[0]);
  };
  const [getAllUsers] = api.endpoints.getAllUsers.useLazyQuery();

  const handleClickSavePic = () => {
    if (editorRef.current) {
      const canvasScaled = editorRef.current.getImageScaledToCanvas();
      canvasScaled.toBlob((blob) => {
        updatePic(user, blob)
          .then(() => {
            showToast(
              SUCCESS,
              intl.formatMessage({
                id: 'AvatarPanel.updateSuccessMessage',
                defaultMessage: 'User profile picture has been updated.',
                description: 'Success message when profile picture is updated',
              }),
            );
            getCurrentUser().then((currentUser) => setCurrentUser(currentUser));
            getAllUsers(user.client);
          })
          .catch(() => {
            showToast(
              ERROR,
              intl.formatMessage({
                id: 'AvatarPanel.updateErrorMessage',
                defaultMessage: 'User Profile picture could not be updated.',
                description: 'Error message when profile picture update fails',
              }),
            );
          });
      });
    }
  };

  const handleScaling = (type) => {
    if (type === 'Up') {
      setScale(scale + 0.1);
    } else {
      setScale(scale <= 0 ? 0 : scale - 0.1);
    }
  };

  const removeAvatar = () => {
    removePic()
      .then(() => {
        showToast(
          SUCCESS,
          intl.formatMessage({
            id: 'AvatarPanel.removeSuccessMessage',
            defaultMessage: 'User profile picture has been removed.',
            description: 'Success message when profile picture is removed',
          }),
        );
        getCurrentUser().then((currentUser) => setCurrentUser(currentUser));
        getAllUsers(user.client);
        hideRemoveAvatarModal();
      })
      .catch(() => {
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'AvatarPanel.removeErrorMessage',
            defaultMessage: 'User Profile picture could not be removed.',
            description: 'Error message when profile picture removal fails',
          }),
        );
      });
  };

  const [removeAvatarModal, showRemoveAvatarModal, hideRemoveAvatarModal] =
    useDeleteModal({
      children: (
        <FormattedMessage
          id="AvatarPanel.removeWarningText"
          defaultMessage="Removing profile picture is not reversible"
          description="Warning text in remove avatar modal"
        />
      ),
      title: intl.formatMessage({
        id: 'AvatarPanel.removeModalTitle',
        defaultMessage: 'Remove profile picture?',
        description: 'Title for remove avatar modal',
      }),
      primaryActionText: intl.formatMessage({
        id: 'AvatarPanel.removeActionText',
        defaultMessage: 'Remove',
        description: 'Remove button text in modal',
      }),
      onDelete: removeAvatar,
    });

  const openUploadDialog = () => {
    if (dropzoneRef.current) {
      dropzoneRef.current.open();
    }
  };

  const handleCancelPic = () => {
    setImage('');
  };
  return (
    <Dropzone
      ref={dropzoneRef}
      noClick
      noKyboard
      onDrop={handleDrop}
      style={styles.Dropzone}
    >
      {({ getRootProps, getInputProps }) => {
        return (
          <div {...getRootProps()}>
            {image ? (
              <Layout w={layoutSize} direction="column">
                <AvatarEditor
                  ref={editorRef}
                  crossOrigin="anonymous"
                  width={avatarSize}
                  height={avatarSize}
                  border={border}
                  color={[255, 255, 255, 0.6]} // RGBA
                  scale={scale}
                  rotate={0}
                  image={image}
                />
                <Layout justify="center">
                  <Layout preset="buttons">
                    <IconButton
                      icon="plus-circle"
                      text=""
                      tooltip={intl.formatMessage({
                        id: 'AvatarPanel.zoomInTooltip',
                        defaultMessage: 'Zooming in the picture',
                        description: 'Tooltip for zoom in button',
                      })}
                      onClick={() => handleScaling('Up')}
                    />
                    <IconButton
                      icon="remove"
                      text=""
                      tooltip={intl.formatMessage({
                        id: 'AvatarPanel.zoomOutTooltip',
                        defaultMessage: 'Zooming out the picture',
                        description: 'Tooltip for zoom out button',
                      })}
                      onClick={() => handleScaling('Down')}
                    />
                  </Layout>
                </Layout>
                <Layout justify="center">
                  <Grid columns={2} columnSpacing={4} rowSpacing={4}>
                    <Button
                      text={intl.formatMessage({
                        id: 'AvatarPanel.savePictureButton',
                        defaultMessage: 'Save Picture',
                        description: 'Save picture button text',
                      })}
                      onClick={handleClickSavePic}
                    />
                    <Button
                      text={intl.formatMessage({
                        id: 'AvatarPanel.cancelButton',
                        defaultMessage: 'Cancel',
                        description: 'Cancel button text',
                      })}
                      onClick={handleCancelPic}
                    />
                  </Grid>
                </Layout>
              </Layout>
            ) : (
              <Layout w={avatarSize} direction="column" spacing={1}>
                <Image
                  src={!Boolean(user.avatar) ? userProfileEmpty : user.avatar}
                  style={styles.avatartImg}
                  alt={intl.formatMessage({
                    id: 'AvatarPanel.avatarImageAlt',
                    defaultMessage: 'avatar image',
                    description: 'Alt text for avatar image',
                  })}
                />
                <Button
                  shouldTruncate
                  textVariant="tiny"
                  onClick={openUploadDialog}
                  text={intl.formatMessage({
                    id: 'AvatarPanel.uploadButtonText',
                    defaultMessage: 'Upload Picture or Drag and Drop',
                    description: 'Upload button text',
                  })}
                />
                {Boolean(user.avatar) ? (
                  <Button
                    onClick={showRemoveAvatarModal}
                    text={intl.formatMessage({
                      id: 'AvatarPanel.removeButtonText',
                      defaultMessage: 'Remove',
                      description: 'Remove button text',
                    })}
                    variant="tertiary"
                  />
                ) : null}
                {removeAvatarModal}
              </Layout>
            )}
            <input {...getInputProps()} />
          </div>
        );
      }}
    </Dropzone>
  );
};
